import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Menu } from '../../components/Menu';
import type { MenuItemCustom } from '../../components/Menu/types';

interface ColorPickerProps {
  activeColor: string;
  onColorChange: (color: string) => void;
}

export const ANNOTATION_COLORS = [
  '#ff0000', // red
  '#ff8800', // orange
  '#ffdd00', // yellow
  '#00cc00', // green
  '#00cccc', // turquoise
  '#0066ff', // blue
  '#8800ff', // purple
  '#ff66cc', // pink
  '#ffffff', // white
  '#000000', // black
];

export const ColorPicker = React.memo(function ColorPicker({
  activeColor,
  onColorChange,
}: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const items: MenuItemCustom[] = useMemo(
    () =>
      ANNOTATION_COLORS.map((color) => ({
        id: color,
        type: 'custom' as const,
        render: () => (
          <span
            className={`kollab-annotation-color-option${color === activeColor ? ' kollab-annotation-color-option--active' : ''}`}
            style={{ backgroundColor: color }}
            aria-label={color}
          />
        ),
        onClick: () => {
          onColorChange(color);
          setIsOpen(false);
        },
      })),
    [activeColor, onColorChange],
  );

  return (
    <div className="kollab-annotation-color-picker">
      <button
        ref={buttonRef}
        className="kollab-annotation-toolbar-btn kollab-annotation-color-btn"
        onClick={handleToggle}
        aria-label={`Color: ${activeColor}`}
        aria-haspopup="true"
        aria-expanded={isOpen}
        data-tooltip="Color"
        type="button"
      >
        <span
          className="kollab-annotation-color-swatch"
          style={{ backgroundColor: activeColor }}
        />
      </button>
      <div className="kollab-annotation-color-menu">
        <Menu
          items={items}
          isOpen={isOpen}
          onClose={handleClose}
          ariaLabel="Select color"
          triggerRef={buttonRef}
          layout="grid"
          gridColumns={5}
        />
      </div>
    </div>
  );
});
